/** Dashboard page (overview cards, empty states) + operator control console. */
export const dashboard = {
  // Overview / dashboard landing page.
  overview: {
    title: "Overview",
    description: "Here's what's happening in your workspace today.",
    summaryError: {
      title: "Couldn't load your workspace overview",
      description:
        "Something went wrong while loading your workspace stats. Check your connection and try again.",
    },
    cards: {
      devices: "Devices",
      onlineTotal: "Online / Total",
      locations: "Locations",
      playlists: "Playlists",
      library: "Library",
      members: "Members",
      schedules: "Schedules",
    },
    recentActivity: "Recent Activity",
    activityError: {
      title: "Couldn't load recent activity",
      description:
        "Something went wrong while loading your workspace activity. Check your connection and try again.",
    },
    noActivity: "No recent activity to show.",
    system: "System",
    removedUser: "Removed user",
    viewAuditTrail: "Full audit trail",
    getStarted: {
      title: "Let's get your first screen playing",
      description:
        "Your music, schedule and screen are set up. Claim a screen and activate it to go live, or preview the player right now to hear your starter station.",
      primary: "Set up a screen",
      secondary: "Preview the player",
    },
  },

  // Control page shell (permission/error/loading states).
  control: {
    playerFallback: "Player",
    notAvailable: {
      title: "Control not available",
      description:
        "Your role doesn't include device control for this workspace. Ask an admin to grant the control permission.",
    },
    notFound: "Player not found.",
    loadError: {
      title: "Couldn't load player",
      description:
        "Something went wrong while loading this player. Check your connection and try again.",
    },
    updateChannelsError: "Could not update channels",
  },

  // Control device picker (choose a player to remote-control).
  devicePicker: {
    actionLabel: "Control this player",
    title: "Choose a player to control",
    subtitle: "Select the screen you want to remote-control from this device.",
  },

  // Per-channel control card.
  channel: {
    mismatch: "Mismatch",
    enableAria: "Enable {channel} channel",
    notInScope: "Not in your channel scope",
    expected: "Expected: ",
    scheduleSuffix: "· {name}",
    noSlot: "No schedule slot now",
    liveHeading: "Live",
    liveStateSuffix: " · {state}",
    scheduledNotPlaying: "Scheduled slot (not playing)",
    liveIdle: "Live idle, waiting for playback",
    playerOfflineStatus: "Player offline, live status unavailable",
    idleNothing: "Idle, nothing playing",
    disabled: "Channel disabled",
    previousAria: "Previous on {channel}",
    pauseAria: "Pause {channel}",
    playAria: "Play {channel}",
    nextAria: "Next on {channel}",
    /**
     * DELIBERATELY device-wide wording. All audio channels share ONE output
     * bus, so any per-card mute/volume acts on the device master — the old
     * "Mute {channel}" labels made one misclick on Announcements silently
     * zero all three cards while audio kept "playing" at gain 0, and the
     * operator had no way to know why (2026-08-14 deep sweep, F3).
     */
    muteAria: "Mute device output ({channel} card)",
    unmuteAria: "Unmute device output ({channel} card)",
    volumeAria: "Device output volume ({channel} card)",
    mutedByMaster: "Device output muted",
    // Resolved-slot fallback labels, selected by the slot's source.
    sourceLive: "Live",
    sourceException: "Exception",
    sourceOff: "Off",
    sourceScheduled: "Scheduled",
  },

  // Control library panel (browse + quick-play).
  library: {
    title: "Library",
    searchPlaceholder: "Search playlists & tracks…",
    sendTracksTo: "Send tracks to",
    noChannels: "No channels available to target.",
    noMatches: "No matching playlists or tracks.",
    noTracks: "No matching tracks.",
    noPlaylists: "No playlists yet.",
    playlists: "Playlists",
    tracks: "Tracks",
    browseCatalogue: "Browse the catalogue",
    catalogue: "Catalogue",
    backToLibrary: "Back to library",
    playlistTitle: "Playlist",
    itemsCount: "{count} items",
    emptyPlaylist: "This playlist is empty.",
    untitled: "Untitled",
    itemFallback: "item",
    playItemAria: "Play {name}",
    timedPlayAria: "Timed play options for {name}",
    playNow: "Play now",
    thisPlayer: "This player",
    wholeLocation: "Whole location",
    playFor: "Play for {duration}",
    // Browse-dimension toggle labels (plural).
    dim: {
      genres: "Genres",
      moods: "Moods",
      venues: "Venues",
    },
    // Active-filter labels (singular).
    genre: "Genre",
    mood: "Mood",
    venue: "Venue",
    // Quick-play duration choices.
    choice: {
      min30: "30 min",
      hour1: "1 hour",
      hours2: "2 hours",
      untilEndOfDay: "Until end of day",
    },
  },

  // Persisted quick-play chip + toasts.
  quickPlay: {
    chipPrefix: "Quick play: {label}",
    wholeLocation: " · whole location",
    endsAt: " · ends {time}",
    stop: "Stop",
    stopAria: "Stop quick play {label}",
    stopError: "Could not stop quick play",
    startedToast: "Quick play started ({duration})",
    startError: "Could not start quick play",
  },

  // Live operations panel (push-to-talk).
  live: {
    title: "Live operations",
    reconnecting: "Reconnecting to control channel…",
    release: "Release",
    keepHolding: "Keep holding… {seconds}",
    holdToTalk: "Hold to talk ({seconds}s)",
    pushToTalkOffPrefix: "Push-to-talk is off for this device. Enable",
    liveBroadcast: "Live broadcast",
    // Only used by components/controls/live-controls-panel.tsx, which nothing
    // renders today. Path matches the real UI: select the device on Sites,
    // then the bulk "Configure" action (Features → Live broadcast).
    pushToTalkOffSuffix:
      " for this device under Sites → Configure to talk live to its announcement channel.",
  },

  // Location sync panel.
  sync: {
    title: "Location sync",
    thisLocation: "This location",
    onlineSummary: " · {online}/{total} online",
    inSyncSuffix: " · in sync",
    checkDevicesSuffix: " · check devices",
    divergenceWarning:
      "Schedules diverge on {buses}: {devices} resolve different content, so this group can't fully sync. Align their schedules or split the group.",
    customGroupNote: "In custom groups: {devices}.",
    customGroupDevice: "{name} → {group}",
    prevAll: "Prev all",
    pauseAll: "Pause all",
    playAll: "Play all",
    skipAll: "Skip all",
    playing: "Playing",
    /**
     * Only ever shown for a genuinely empty group. The panel used to render
     * this (with "0/0 online") for a request that was still loading or had
     * failed, which is self-contradictory: the panel is mounted only when the
     * client has already counted two or more devices at the location.
     */
    noOtherDevices: "No other devices in this sync group.",
    loading: "Checking devices…",
    /** The group is unknown, NOT known to be empty — never claim a count. */
    unavailable: "Sync status unavailable. Retrying.",
  },

  // Forward schedule timeline.
  timeline: {
    title: "Timeline",
    now: "Now: {time}",
    nowLabel: "Now",
    endsArrow: "→ {time}",
    comingUp: "Coming up",
    nextHours: "(next {hours}h)",
    loadError: "Couldn't load the schedule for this player.",
    nothingScheduled: "Nothing scheduled in the next {hours} hours.",
    offAir: "Off air",
    liveBroadcast: "Live broadcast",
    scheduled: "Scheduled",
    conflict: {
      one: "{count} channel has overlapping schedule windows. Review daypart rules in Fleet.",
      other:
        "{count} channels have overlapping schedule windows. Review daypart rules in Fleet.",
    },
    // Scheduled-item kind badge labels.
    badge: {
      exception: "Exception",
      off: "Off",
      live: "Live",
    },
  },

  // Now-playing hero (web player / mirror).
  nowPlaying: {
    nothingPlaying: "Nothing playing",
    videoSignage: "Video signage",
    visualSignageDesc: "Playing visual signage for this venue.",
  },

  // Upcoming queue list.
  queue: {
    empty: "Queue is empty.",
    upNext: "Up next",
    artistSuffix: " · {artist}",
  },
} as const;
